import { COUNTRY_TO_CURRENCY } from "./countryCurrency";

export const INVESTMENT_TIER_GROUPS = [
  { name: "Start up", amounts: [1000, 2000, 3000, 4000, 5000] },
  { name: "Pro plans", amounts: [10000, 20000, 30000, 40000, 50000] },
  { name: "VIP plans", amounts: [100000, 200000, 300000, 400000, 500000] },
];

const RATE_URL = "https://open.er-api.com/v6/latest/ZAR";
const RATE_CACHE_KEY = "profit-monitoring:fx:ZAR:v1";

function normalizeCountryName(value) {
  return String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

const REGION_NAMES = typeof Intl.DisplayNames === "function"
  ? new Intl.DisplayNames(["en"], { type: "region" })
  : null;
const COUNTRY_NAME_TO_CODE = Object.keys(COUNTRY_TO_CURRENCY).reduce((names, code) => {
  const displayName = REGION_NAMES && REGION_NAMES.of(code);
  if (displayName) names[normalizeCountryName(displayName)] = code;
  return names;
}, {
  usa: "US",
  "united states": "US",
  "united states of america": "US",
  "u s": "US",
  "u s a": "US",
});

function resolveCountryCode(value) {
  const normalized = String(value || "").trim().toUpperCase();
  if (COUNTRY_TO_CURRENCY[normalized]) return normalized;
  return COUNTRY_NAME_TO_CODE[normalizeCountryName(value)] || "";
}

export function getAccountCurrencyCode(user) {
  const country = user?.country;
  const candidates = [
    country && typeof country === "object" ? country.code : "",
    user?.countryCode,
    country && typeof country === "object" ? country.name : country,
  ];
  for (const candidate of candidates) {
    const countryCode = resolveCountryCode(candidate);
    if (countryCode && COUNTRY_TO_CURRENCY[countryCode]) return COUNTRY_TO_CURRENCY[countryCode];
  }
  return "";
}

export function getBrowserRegionCurrencyCode(locale) {
  const defaultLocale = typeof navigator !== "undefined" ? navigator.language : "";
  const locales = Array.isArray(locale) ? locale : [locale || defaultLocale];
  for (const candidate of locales) {
    const value = String(candidate || "").trim().replace(/_/g, "-");
    if (!value) continue;
    let region = "";
    try {
      if (typeof Intl.Locale === "function") region = new Intl.Locale(value).region || "";
    } catch (error) {
      // A malformed browser locale should not prevent account currency display.
    }
    if (!region) {
      const parts = value.split("-").slice(1);
      region = parts.find((part) => /^[A-Z]{2}$/i.test(part)) || "";
    }
    const currencyCode = COUNTRY_TO_CURRENCY[String(region).toUpperCase()];
    if (currencyCode) return currencyCode;
  }
  return "";
}

function readFreshCachedRates() {
  try {
    const cached = JSON.parse(localStorage.getItem(RATE_CACHE_KEY) || "null");
    const nextUpdate = Date.parse(cached?.time_next_update_utc || "");
    if (cached?.result === "success" && cached.base_code === "ZAR" && cached.rates && Number.isFinite(nextUpdate) && nextUpdate > Date.now()) return cached;
  } catch (error) {
    // Storage can be unavailable in private browsing; continue with a network request.
  }
  return null;
}

export async function getZarRateTable() {
  const cached = readFreshCachedRates();
  if (cached) return cached;
  const response = await fetch(RATE_URL, { headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error("Local currency rates are temporarily unavailable.");
  const data = await response.json();
  if (data?.result !== "success" || data.base_code !== "ZAR" || !data.rates || Number(data.rates.ZAR) !== 1) {
    throw new Error("The currency service returned an invalid rate table.");
  }
  try {
    localStorage.setItem(RATE_CACHE_KEY, JSON.stringify(data));
  } catch (error) {
    // A successful lookup remains usable even when browser storage is unavailable.
  }
  return data;
}

export function convertZarAmount(amount, rates, currencyCode) {
  const numericAmount = Number(amount);
  const code = String(currencyCode || "").trim().toUpperCase();
  if (!Number.isFinite(numericAmount) || numericAmount < 0 || !code) return null;
  if (code === "ZAR") return numericAmount;
  const rate = Number(rates?.[code]);
  return Number.isFinite(rate) && rate > 0 ? numericAmount * rate : null;
}

export function formatPlanCurrency(value, currencyCode) {
  const amount = Number(value);
  const code = /^[A-Z]{3}$/.test(String(currencyCode || "")) ? currencyCode : "ZAR";
  if (!Number.isFinite(amount)) return "—";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: code }).format(amount);
  } catch (error) {
    return code + " " + amount.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
}
